import { Close } from '@mui/icons-material';
import { Box, Chip, Stack, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';

import { StyledIconButton } from '../../../theme/CustomComponents';
import { DataRequestItem } from '../../../Types/dealGPT';
import { Commentable, DataRequestListView } from '../../../Types/enums';
import AutoTruncatingTypographyWithTooltip from '../../AutoTruncatingTypographyWithTooltip';
import Comments from '../../Comments/Comments';
import { useDrawer } from '../../Providers/DrawerProvider';
import DataRequestItemOverview from './DataRequestItemOverview';
import DataRequestListItemHistory from './DataRequestListItemHistory';

export interface DataRequestItemDrawerProps {
  item: DataRequestItem;
  refetchItems: () => Promise<void>;
}

const DataRequestItemDrawer: React.FC<DataRequestItemDrawerProps> = ({ item, refetchItems }) => {
  const [itemState, setItemState] = useState<DataRequestItem>(item);
  const [selectedView, setSelectedView] = useState<DataRequestListView>(
    DataRequestListView.OVERVIEW
  );
  const { closeDrawer } = useDrawer();

  const onItemChanged = useCallback(
    (newItem: DataRequestItem) => {
      setItemState(newItem);
      refetchItems();
    },
    [refetchItems]
  );

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: '95px',
          display: 'flex',
          alignItems: 'center',
          padding: 2,
          justifyContent: 'space-between',
          borderBottom: '1px solid #ddd',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'start' }}>
          <Typography variant="h5" sx={{ color: '#50658E' }}>
            {item.key}
          </Typography>
          <AutoTruncatingTypographyWithTooltip
            text={item.scope}
            variant="h4"
            sx={{ fontWeight: 700 }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <StyledIconButton onClick={closeDrawer}>
            <Close />
          </StyledIconButton>
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" height="100%" gap={2} padding={2}>
        <Stack direction="row" spacing={1}>
          {Object.values(DataRequestListView).map((view) => (
            <Chip
              variant={view === selectedView ? 'filled' : 'outlined'}
              key={view}
              label={
                <Typography
                  variant="body2"
                  sx={view === selectedView ? { color: 'white' } : { color: '#070154' }}
                >
                  {view}
                </Typography>
              }
              onClick={() => setSelectedView(view)}
              sx={{
                py: 2,
                borderRadius: '16px',
                backgroundColor: view === selectedView ? '#070154' : 'white',
                ...(view === selectedView && {
                  '&:hover': { backgroundColor: '#0047FF' },
                }),
              }}
            />
          ))}
        </Stack>
        {selectedView === DataRequestListView.OVERVIEW && (
          <DataRequestItemOverview item={itemState} onItemChanged={onItemChanged} />
        )}
        {selectedView === DataRequestListView.COMMENTS && (
          <Box
            sx={{
              height: 'calc(100vh - 200px)',
            }}
          >
            <Comments entityId={itemState.id} entityType={Commentable.DataRequestItem} />
          </Box>
        )}
        {selectedView === DataRequestListView.HISTORY && (
          <Box
            sx={{
              height: 'calc(100vh - 200px)',
              overflowY: 'auto',
            }}
          >
            <DataRequestListItemHistory item={itemState} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DataRequestItemDrawer;
